/**
 * Canonical capture processing schema — frontend mirror of functions/src/captureSchema.ts
 */

export const CAPTURE_PROFILE = {
  STANDARD: 'standard',
  THREE_SIXTY: '360',
};

export function normalizeCaptureProfile(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === '360' || s === 'equirect_360' || s === 'equirectangular') {
    return CAPTURE_PROFILE.THREE_SIXTY;
  }
  return CAPTURE_PROFILE.STANDARD;
}

export function captureProfileToBackend(profile) {
  return profile === CAPTURE_PROFILE.THREE_SIXTY ? 'equirect_360' : 'orbital';
}

/** Raw INSV vs MP4/MOV video upload (processing.sourceFormat). */
export const SOURCE_FORMAT = {
  INSV: 'insv',
  VIDEO: 'video',
};

const LEGACY_SOURCE_FORMAT_VIDEO_ALIASES = new Set([
  SOURCE_FORMAT.VIDEO,
  'equirect',
  'stitched',
  'equirect_stitched',
  'mp4',
]);

export function normalizeSourceFormat(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === SOURCE_FORMAT.INSV || s === 'raw_insv' || s === 'raw-insv') {
    return SOURCE_FORMAT.INSV;
  }
  if (LEGACY_SOURCE_FORMAT_VIDEO_ALIASES.has(s)) {
    return SOURCE_FORMAT.VIDEO;
  }
  return SOURCE_FORMAT.VIDEO;
}

export const INSV_VENDOR = {
  INSTA360: 'insta360',
  ANTIGRAVITY: 'antigravity',
  UNKNOWN: 'unknown',
};

export function normalizeInsvVendor(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === INSV_VENDOR.INSTA360 || s === 'insta') return INSV_VENDOR.INSTA360;
  if (s === INSV_VENDOR.ANTIGRAVITY || s === 'ag') return INSV_VENDOR.ANTIGRAVITY;
  return INSV_VENDOR.UNKNOWN;
}

export const INSV_INPUT_MODE = {
  SINGLE: 'single',
  PAIR: 'pair',
};

export const UPLOADS_RAW_REL = 'uploads_raw';
export const STITCH_MANIFEST_REL = 'data/stitch_manifest.json';

export const SFM_MATCHING_METHOD = {
  SEQUENTIAL: 'sequential',
  EXHAUSTIVE: 'exhaustive',
};

export function normalizeSfmMatchingMethod(raw) {
  const m = String(raw || SFM_MATCHING_METHOD.SEQUENTIAL).trim().toLowerCase();
  if (m === 'exhaustive') {
    return SFM_MATCHING_METHOD.EXHAUSTIVE;
  }
  return SFM_MATCHING_METHOD.SEQUENTIAL;
}

export function sfmMatchingMethodToColmap(method) {
  if (method === SFM_MATCHING_METHOD.EXHAUSTIVE) return 'exhaustive';
  return 'sequential';
}

/** First-pass COLMAP matcher. Orbital and 360 both start with sequential matching. */
export function firstPassSfmMatchingMethodForProfile(profile) {
  void profile;
  return SFM_MATCHING_METHOD.SEQUENTIAL;
}

export const FAILED_AT = {
  UPLOAD: 'upload',
  FRAME_EXTRACTION: 'frame extraction',
  STRUCTURE_FROM_MOTION: 'structure from motion',
  GAUSSIAN_TRAINING: 'gaussian training',
  SOG_CONVERSION: 'SOG conversion',
};

export function failedAtToStageIndex(failedAt) {
  const n = String(failedAt || '').trim().toLowerCase();
  if (!n) return null;
  if (n === FAILED_AT.UPLOAD || n.includes('uploadpending')) return 1;
  if (n === FAILED_AT.FRAME_EXTRACTION || (n.includes('frame') && n.includes('extract')) || n === 'video2images') return 2;
  if (n === FAILED_AT.STRUCTURE_FROM_MOTION || n === 'sfm' || n.includes('structure from motion') || n.includes('3d reconstruction')) return 3;
  if (n === FAILED_AT.GAUSSIAN_TRAINING || n.includes('gaussian') || n.includes('generating')) return 4;
  if (n === FAILED_AT.SOG_CONVERSION || n.includes('sog') || n.includes('finalizing') || n.includes('viewer ready')) return 5;
  return null;
}

export function stageIndexToFailedAt(idx) {
  switch (idx) {
  case 1: return FAILED_AT.UPLOAD;
  case 2: return FAILED_AT.FRAME_EXTRACTION;
  case 3: return FAILED_AT.STRUCTURE_FROM_MOTION;
  case 4: return FAILED_AT.GAUSSIAN_TRAINING;
  case 5: return FAILED_AT.SOG_CONVERSION;
  default: return null;
  }
}

export function coerceFailedStageIndex(explicit, failedAt) {
  const fromLabel = failedAtToStageIndex(failedAt);
  if (fromLabel != null) return fromLabel;
  if (typeof explicit !== 'number' || !Number.isFinite(explicit)) return null;
  if (explicit >= 1 && explicit <= 5) return explicit;
  if (explicit >= 0 && explicit <= 3) return explicit + 1;
  if (explicit === -1) return null;
  return null;
}

export function isNonPipelineFailure(failedAt) {
  const n = String(failedAt || '').trim().toLowerCase();
  return n === 'dispatch' || n === 'cancelled' || n === 'unknown';
}

export const SPARSE_SFM_FAILURE_CODE = 'SFM_UNSAT_MATCHES';

export function buildSparseSfmFailureDetail(matched, total) {
  return `SfM too sparse. (${matched}/${total} images matched)`;
}

/** Parse matched/total from sparse SfM failure strings (including 360 coverage gate). */
export function parseSfmSparseCounts(text) {
  if (!text || typeof text !== 'string') return null;
  const registered = text.match(/\((\d+)\/(\d+)\s+images\s+(?:registered|matched)\)/i);
  if (registered) {
    return { matched: Number(registered[1]), total: Number(registered[2]) };
  }
  const coverageRatio = text.match(/registration ratio[^()]*\((\d+)\/(\d+)\)/i);
  if (coverageRatio) {
    return { matched: Number(coverageRatio[1]), total: Number(coverageRatio[2]) };
  }
  const matchedFrames = text.match(/matched\s+(\d+)\s*\/\s*(\d+)\s+frames?/i);
  if (matchedFrames) {
    return { matched: Number(matchedFrames[1]), total: Number(matchedFrames[2]) };
  }
  const slashPair = text.match(/too sparse[^0-9]*(\d+)\s*\/\s*(\d+)/i);
  if (slashPair) {
    return { matched: Number(slashPair[1]), total: Number(slashPair[2]) };
  }
  return null;
}

export function parseSourceVideoResolution(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  const match = s.match(/(\d{2,5})\s*x\s*(\d{2,5})/i);
  if (!match) return null;
  return `${match[1]}x${match[2]}`;
}

export function parseSourceVideoFpsFromLegacy(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  const atMatch = s.match(/@(\d+(?:\.\d+)?)/);
  if (atMatch) {
    const fps = Math.round(Number(atMatch[1]));
    return Number.isFinite(fps) && fps > 0 ? fps : null;
  }
  return null;
}

export function coerceSourceVideoFps(raw) {
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

/** UI segment index (0-based) from canonical 1-based failedStageIndex. */
export function failedStageIndexToUiSegment(idx) {
  const i = coerceFailedStageIndex(idx);
  if (i == null) return -1;
  if (i <= 1) return 0;
  if (i === 2) return 1;
  if (i === 3) return 2;
  return 3;
}
